import { chatSidebarOpenAtom } from '@/atoms/chatSidebarAtoms';
import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import {
  IS_CALLS_ENABLED,
  IS_COMMS_SELF_SERVE_ENABLED,
  IS_PROTOCOLS_ENABLED,
  IS_SIDEBAR_PANEL_ENABLED,
} from '@/components/constants/flags';
import { type FooterMoreRowDescriptor } from '@/components/layout/Navigation/sidebar/footer-rail/footer-rail-context';
import {
  useDocumentInboxGate,
  useIsAgentHomeV1Enabled,
  useIsClinicalCodingDashboardEnabled,
  useIsEvidenceSidePanelDisabledInEhrLaunch,
  useIsPatientListEnabled,
  useIsProjectsEnabled,
  useIsRepeatsPocEnabled,
  useIsStandaloneCalculatorsEnabled,
  useIsTriagePocEnabled,
} from '@/features/flags';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useTranslatorAccess } from '@/hooks/useTranslatorAccess';
import { getBaseChatPath } from '@heidi/agents/contracts';
import { useCanUseWorkHome, useResetProjectsComposerContext } from '@heidi/agents/data';
import { useCodingDashboardPermissions } from '@heidi/coding';
import { Evidence } from '@heidi/design-system/icons';
import { useSetAtom } from 'jotai';
import {
  Calculator,
  ClipboardList,
  Inbox,
  Languages,
  Phone,
  Pill,
  Stethoscope,
  Tv2,
  UsersRound,
  Zap,
} from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo, useTransition } from 'react';
import { useIntl } from 'react-intl';
import { useMobilePanel } from '../mobile/use-mobile-panel';
import { useCollapsedFooterDescriptors } from './useCollapsedFooterDescriptors';
import { useConciergeMoreDescriptor } from './useConciergeMoreDescriptor';
import { useIsRouteActive } from './useIsRouteActive';
import { useRoutinesMoreDescriptor } from './useRoutinesMoreDescriptor';

/**
 * Builds the descriptor list for the primary-nav More dropdown (MUL-3702).
 * Applies all item-level flag gates — items that are off are excluded.
 * Descriptors are memoized; only the filtered list changes when flags change.
 */
export function usePrimaryNavMoreDescriptors(): readonly FooterMoreRowDescriptor[] {
  const intl = useIntl();
  const { dismissMobileSidebar } = useMobilePanel();
  const isRouteActive = useIsRouteActive();
  const isEhrLaunch = useIsEhrLaunch();
  const router = useRouter();
  const [, startTransition] = useTransition();
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isProjects = useIsProjectsEnabled();
  const setChatSidebarOpen = useSetAtom(chatSidebarOpenAtom);
  const resetProjectsComposerContext = useResetProjectsComposerContext();

  const isProjectsHome =
    (router.pathname === '/scribe/chat/projects' ||
      router.pathname.startsWith('/scribe/chat/projects/')) &&
    !router.query.id?.length;

  // Item-level flags
  const isPatientsEnabled = useIsPatientListEnabled();
  const { isTranslatorShown } = useTranslatorAccess();
  const enableRefills = useIsRepeatsPocEnabled();
  const inboxGate = useDocumentInboxGate();
  const enableProtocols = IS_PROTOCOLS_ENABLED();
  const isCodingEnabled = useIsClinicalCodingDashboardEnabled();
  const { canViewDashboard } = useCodingDashboardPermissions();
  const routinesDescriptor = useRoutinesMoreDescriptor();
  const conciergeDescriptor = useConciergeMoreDescriptor();
  const isCalculatorsEnabled = useIsStandaloneCalculatorsEnabled();
  const enableTriage = useIsTriagePocEnabled();
  const isCallsEnabled = IS_CALLS_ENABLED() ?? false;
  const enableCalls = !isEhrLaunch && (isCallsEnabled || IS_COMMS_SELF_SERVE_ENABLED());
  const collapsedFooterDescriptors = useCollapsedFooterDescriptors();

  // Evidence / Work
  const {
    isSessionlessEvidenceShown: enableSessionlessEvidence,
    isNhsUser,
    showManagedTeamActivationRequest,
  } = useEvidenceAccess();
  const showWorkNav = useCanUseWorkHome();
  const evidenceHref = getBaseChatPath(showWorkNav);
  const isAgentHomeV1Enabled = useIsAgentHomeV1Enabled();
  const isEvidenceDisabledInEhrLaunch = useIsEvidenceSidePanelDisabledInEhrLaunch();
  const showEvidence =
    !isAgentHomeV1Enabled &&
    !(isEhrLaunch && isEvidenceDisabledInEhrLaunch) &&
    (enableSessionlessEvidence ||
      isNhsUser ||
      showManagedTeamActivationRequest ||
      showWorkNav);

  const dismiss = () => {
    setSidebarOpenTab(undefined);
    dismissMobileSidebar();
  };

  return useMemo<readonly FooterMoreRowDescriptor[]>(() => {
    const items: FooterMoreRowDescriptor[] = [];

    if (isPatientsEnabled && !isEhrLaunch) {
      items.push({
        id: 'patients',
        label: intl.formatMessage({ defaultMessage: 'Patients', id: '1eiSDTlf+P' }),
        icon: <UsersRound />,
        href: '/patients',
        testId: 'sidebar-link-patients',
        isActive: isRouteActive('/patients'),
        onSelect: dismiss,
      });
    }

    if (isTranslatorShown) {
      items.push({
        id: 'translator',
        label: intl.formatMessage({
          defaultMessage: 'Translator',
          description: 'Sidebar nav item for the Translator tab',
          id: 'WrkGIkyMQI',
        }),
        icon: <Languages />,
        href: '/translator',
        testId: 'sidebar-link-view-translator',
        isActive: isRouteActive('/translator'),
        onSelect: dismiss,
      });
    }

    if (enableRefills) {
      items.push({
        id: 'refills',
        label: intl.formatMessage({
          defaultMessage: 'Refills',
          id: 'zzv70DcujP',
          description: 'Sidebar entry for the repeat-prescription review queue',
        }),
        icon: <Pill />,
        href: '/refills',
        testId: 'sidebar-link-view-refills',
        isActive: isRouteActive('/refills'),
        onSelect: dismiss,
      });
    }

    if (inboxGate === 'on') {
      items.push({
        id: 'inbox',
        label: intl.formatMessage({ defaultMessage: 'Inbox', id: 'VCMKbtL+3b' }),
        icon: <Inbox />,
        href: '/inbox',
        testId: 'sidebar-link-inbox',
        isActive: isRouteActive('/inbox'),
        onSelect: dismiss,
      });
    }

    if (showEvidence) {
      items.push({
        id: 'evidence',
        label: showWorkNav
          ? 'Work'
          : intl.formatMessage({
              defaultMessage: 'Evidence',
              id: 'JGX1+3qRdx',
              description: 'Sidebar nav label for the Evidence section',
            }),
        icon: showWorkNav ? <Tv2 /> : <Evidence />,
        href: evidenceHref,
        testId: 'sidebar-link-view-evidence',
        isActive: isRouteActive(evidenceHref),
        onSelect: () => {
          if (showManagedTeamActivationRequest) return;
          if (isProjects && isProjectsHome) resetProjectsComposerContext();
          startTransition(() => {
            setSidebarOpenTab(undefined);
            if (!isSidebarPanelEnabled) setChatSidebarOpen(true);
          });
          dismissMobileSidebar();
        },
      });
    }

    if (enableProtocols) {
      items.push({
        id: 'protocols',
        label: intl.formatMessage({
          defaultMessage: 'Protocols',
          id: 'G7uNlaMmjV',
          description: 'Sidebar nav label for the Protocols section',
        }),
        icon: <Zap />,
        href: '/protocols',
        testId: 'sidebar-link-protocols',
        isActive: isRouteActive('/protocols'),
        onSelect: dismiss,
      });
    }

    if (isCodingEnabled && canViewDashboard) {
      items.push({
        id: 'coding',
        label: intl.formatMessage({ defaultMessage: 'Coding', id: '6RW4LUKcRL' }),
        icon: <ClipboardList />,
        href: '/coding-dashboard',
        testId: 'sidebar-link-view-coding-dashboard',
        isActive: isRouteActive('/coding-dashboard'),
        onSelect: dismiss,
      });
    }

    if (routinesDescriptor) items.push(routinesDescriptor);

    if (isCalculatorsEnabled) {
      items.push({
        id: 'calculators',
        label: intl.formatMessage({
          defaultMessage: 'Calculators',
          id: 'uoElHXX3aQ',
          description: 'Sidebar nav label for the Calculators section',
        }),
        icon: <Calculator />,
        href: '/scribe/calculators',
        testId: 'sidebar-link-calculators',
        isActive: isRouteActive('/scribe/calculators'),
        onSelect: dismiss,
      });
    }

    if (enableTriage) {
      items.push({
        id: 'triage',
        label: intl.formatMessage({
          defaultMessage: 'Triage',
          id: 'vkZIxlzh2U',
          description: 'Sidebar nav label for the Triage section',
        }),
        icon: <Stethoscope />,
        href: '/triage',
        testId: 'sidebar-link-view-triage',
        isActive: isRouteActive('/triage'),
        onSelect: dismiss,
      });
    }

    if (conciergeDescriptor) items.push(conciergeDescriptor);

    if (enableCalls) {
      items.push({
        id: 'calls',
        label: intl.formatMessage({
          defaultMessage: 'Comms',
          id: 'p1DwQZi8mj',
          description: 'Sidebar nav label for the Calls/Comms section',
        }),
        icon: <Phone />,
        href: '/comms',
        testId: 'sidebar-link-view-calls',
        isActive: isRouteActive('/comms'),
        onSelect: dismiss,
      });
    }

    return [...items, ...collapsedFooterDescriptors];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPatientsEnabled,
    isEhrLaunch,
    isTranslatorShown,
    enableRefills,
    inboxGate,
    showEvidence,
    showWorkNav,
    evidenceHref,
    enableProtocols,
    isCodingEnabled,
    canViewDashboard,
    routinesDescriptor,
    conciergeDescriptor,
    isCalculatorsEnabled,
    enableTriage,
    enableCalls,
    intl,
    isRouteActive,
    collapsedFooterDescriptors,
    showManagedTeamActivationRequest,
    isProjects,
    isProjectsHome,
    resetProjectsComposerContext,
    dismissMobileSidebar,
  ]);
}
